// -----------------------------------------------------------------------------
// Anaglyphic, Netherlands @ 2023.
// See README in the root project for more information.
// -----------------------------------------------------------------------------

import { defineStore } from "pinia";
import { convertDates } from "chartjs-graph";
import { useExplorerStore } from "@/modules/explorer/store";
import { toRawDeep } from "@/Utils/to-raw-deep";
import DocumentDO = App.Data.Explorer.DocumentDO;

// -----------------------------------------------------------------------------
export const useDocumentHistory = defineStore("documentHistory", {
	state: () => {
		return {
			localStorageKey: "motivesDocuments",
			documentHistory: [] as DocumentDO[],
			isLoading: false,
			currentHistoryIndex: null,
		};
	},
	getters: {
		canGoBack: (state) =>
			state.currentHistoryIndex !== null && state.currentHistoryIndex > 0,
		canGoForward: (state) =>
			state.currentHistoryIndex < state.documentHistory.length - 1,
	},
	actions: {
		getMostRecentDocument() {
			useExplorerStore().document = {
				...convertDates(this.documentHistory[0]),
			};
			this.currentHistoryIndex = this.documentHistory.length - 1;
		},

		addDocumentToHistory(appDocument: DocumentDO) {
			if (useExplorerStore().explorerState.documentFetchedFromHistory) {
				return;
			}

			const clone = structuredClone(toRawDeep(appDocument));

			if (this.canGoForward) {
				this.documentHistory = this.documentHistory.slice(
					0,
					this.currentHistoryIndex + 1
				);
			}

			const last = this.documentHistory[this.documentHistory.length - 1];
			if (last?.uuid === clone.uuid) {
				return;
			}

			this.documentHistory.push(clone);

			if (this.documentHistory.length > 50) {
				this.documentHistory.shift();
			}

			this.currentHistoryIndex = this.documentHistory.length - 1;

			this.update();
		},

		getPreviousDocument() {
			if (!this.canGoBack) {
				return;
			}
			useExplorerStore().explorerState.documentFetchedFromHistory = true;
			this.currentHistoryIndex--;
			useExplorerStore().document = {
				...convertDates(this.documentHistory[this.currentHistoryIndex]),
			};
		},

		getNextDocument() {
			if (!this.canGoForward) {
				return;
			}
			useExplorerStore().explorerState.documentFetchedFromHistory = true;
			this.currentHistoryIndex++;
			useExplorerStore().document = {
				...convertDates(this.documentHistory[this.currentHistoryIndex]),
			};
		},

		update() {
			localStorage.setItem(
				this.localStorageKey,
				JSON.stringify(this.documentHistory)
			);
		},
	},
});
